import React, { useState } from 'react';
import './Articles.css';

interface Article {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  imageUrl?: string;
  sourceUrl?: string;
  isExternal: boolean;
  category: 'technology' | 'industry' | 'tutorial' | 'news' | 'insights';
}

const Articles: React.FC = () => {
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [articles] = useState<Article[]>([
    {
      id: '1',
      title: 'Herba Sensors: The Future of Plant Health Technology',
      excerpt: 'Understanding how Herba sensors combine advanced monitoring, automated watering, and AI to transform commercial landscaping management.',
      content: `Understanding Herba Sensors

Herba sensors represent the cutting-edge evolution of plant monitoring technology, deriving their name from the Latin word "herba," meaning plant or vegetation. These advanced monitoring systems combine sensor technology, automated watering, and artificial intelligence to provide real-time, continuous monitoring of plant health, environmental conditions, and physiological responses.

Herba sensors create a comprehensive ecosystem of data collection that transforms how we understand and manage plant health in commercial landscaping and property management.

The Technology Behind Herba Sensors
Environmental Monitoring Capabilities
Herba sensor networks include capabilities to track:
* Watering needs and automated irrigation
* Soil moisture at multiple depths
* Ambient temperature and humidity
* Light intensity and quality

Comprehensive Monitoring Features
Real-Time Monitoring
* Data collection from multiple sensor points
* Alerts via email protect both the property and maintenance teams
* Cloud-based dashboard accessible from anywhere
* Fleet management integration for optimized service routing

Predictive Analytics
* Machine learning algorithms that improve accuracy over time
* Weather integration for proactive adjustments
* Anomaly detection that identifies unusual conditions
* Maintenance scheduling optimization

Fleet Management
Maintenance companies can integrate sensor data with fleet management systems to optimize route planning, prioritize service calls, and deploy crews efficiently based on real-time plant health data.

AI-Powered Analytics
Advanced machine learning algorithms analyze data patterns to:
* Predict plant stress before visible symptoms appear
* Optimize irrigation and fertilization schedules
* Generate automated alerts for critical conditions that protect both property owners and maintenance companies from liability

Why Herba Sensor Investments Deliver Exceptional ROI
1. Water Conservation & Operational Efficiency
ROI Impact:
* Water usage reduction: 25-50% average savings
* Utility cost savings: $15,000-$40,000 annually for large properties
* Labor efficiency: 25% reduction in unnecessary maintenance visits
* Fleet optimization: 30% more efficient routing based on real-time needs

2. Enhanced Property Performance
ROI Impact:
* Property value increase: 7-15% premium for well-maintained landscapes
* Rental revenue increase: 7% higher rates with superior landscaping
* Tenant retention improvement: 15-25% longer lease terms
* Predictive maintenance: 40-60% reduction in emergency calls

Implementation Best Practices
Maximizing ROI
* Start with high-impact areas for immediate returns
* Train staff on system interpretation and response protocols
* Integrate with existing fleet management for maintenance companies
* Document prevented incidents for ROI calculation

Success Metrics
* Track both cost savings and revenue impacts
* Monitor fleet efficiency and make improvements
* Measure liability reduction benefits
* Regular ROI reporting to stakeholders

Conclusion: The Smart Investment Choice
Herba sensors represent more than just monitoring technology—they're a strategic investment in property value protection, operational efficiency, and sustainable management practices.

As climate change increases weather volatility and water scarcity drives up utility costs, proactive plant monitoring becomes essential for maintaining property values and operational efficiency. Properties equipped with Herba sensor networks are better positioned to:
* Maintain consistent landscape quality regardless of environmental challenges
* Optimize resource usage for both cost savings and sustainability goals
* Prevent costly emergencies through early detection and intervention
* Enhance tenant satisfaction and property marketability
* Generate measurable returns on investment year after year

The future of commercial property management is data-driven, predictive, and sustainable. Herba sensors make that future available today.

Start your Herba sensor journey today and transform your property into a smart, efficient, and valuable asset that delivers consistent returns while protecting your landscape investment.`,
      author: 'Herba Team',
      date: '2025-06-08',
      category: 'technology',
      isExternal: false
    },
    {
      id: '2',
      title: 'Plant Selection Guidelines for Your Space',
      excerpt: 'Comprehensive guidelines for selecting the right plants for commercial properties, with expert recommendations and best practices.',
      content: '',
      author: 'Tournesol Siteworks',
      date: '2025-01-05',
      category: 'insights',
      sourceUrl: 'https://tournesol.com/asset/6572171715478/greenscreen_Guidelines-for-Plant-Selection-1.pdf',
      isExternal: true
    },
    {
      id: '3',
      title: 'On Innovation And Automation: What\'s Ahead For The Architecture And Design Industry',
      excerpt: 'Exploring the future of architecture and design through automation, AI, and innovative technologies.',
      content: '',
      author: 'Bisnow',
      date: '2024-12-20',
      category: 'technology',
      sourceUrl: 'https://www.bisnow.com/san-francisco/news/architecture-design/on-innovation-and-automation-whats-ahead-for-the-architecture-and-design-industry-123367',
      isExternal: true
    },
    {
      id: '4',
      title: '5 Ways Commercial Landscape is Important for Business',
      excerpt: 'Explore how professional landscaping impacts business success, from first impressions to employee productivity.',
      content: '',
      author: 'Strategic Grounds',
      date: '2024-12-15',
      category: 'industry',
      sourceUrl: 'https://www.strategicgrounds.com/blog/5-ways-commercial-landscape-is-important-for-the-business/',
      isExternal: true
    },
    {
      id: '5',
      title: 'Impact of Landscape Maintenance on Commercial Property Value',
      excerpt: 'Understanding how regular landscape maintenance directly affects property values and tenant satisfaction.',
      content: '',
      author: 'KD Landscape',
      date: '2024-12-10',
      category: 'insights',
      sourceUrl: 'https://kdlandscapeinc.com/blog/impact-landscape-maintenance-commercial-property-value',
      isExternal: true
    },
    {
      id: '6',
      title: 'How PropTech is Changing the Commercial Real Estate Landscape',
      excerpt: 'Discover how technology is transforming property management and creating new opportunities in commercial real estate.',
      content: '',
      author: 'Partners Real Estate',
      date: '2024-11-28',
      category: 'technology',
      sourceUrl: 'https://partnersrealestate.com/media-center/blog/how-proptech-is-changing-the-commercial-real-estate-landscape/',
      isExternal: true
    },
    {
      id: '7',
      title: 'Designing For Sustainability: Inside This Unique Rooftop Garden And Meditation Space',
      excerpt: 'A look at sustainable design practices in commercial spaces with innovative rooftop gardens.',
      content: '',
      author: 'Bisnow',
      date: '2024-11-25',
      category: 'industry',
      sourceUrl: 'https://www.bisnow.com/los-angeles/news/commercial-real-estate/green-design-tournesolsiteworks-studiob-119869',
      isExternal: true
    },
    {
      id: '8',
      title: 'PropTech: How Technology is Changing Commercial Real Estate',
      excerpt: 'JP Morgan insights on the technological revolution reshaping how we manage and invest in commercial properties.',
      content: '',
      author: 'JP Morgan',
      date: '2024-11-20',
      category: 'technology',
      sourceUrl: 'https://www.jpmorgan.com/insights/real-estate/multifamily/proptech-how-technology-is-changing-commercial-real-estate',
      isExternal: true
    },
    {
      id: '9',
      title: 'Landscape Enhancements to Boost Property Value',
      excerpt: 'Strategic landscaping improvements that deliver the highest return on investment for commercial properties.',
      content: '',
      author: 'Greenscape',
      date: '2024-11-15',
      category: 'industry',
      sourceUrl: 'https://www.greenscape.us.com/landscape-enhacements-to-boost-property-value/',
      isExternal: true
    },
    {
      id: '10',
      title: 'Living Green Walls And Rooftop Gardens: How Landscaping Can Make Buildings More Sustainable',
      excerpt: 'Innovative approaches to sustainable building design through green walls and rooftop gardens.',
      content: '',
      author: 'Bisnow',
      date: '2024-11-10',
      category: 'technology',
      sourceUrl: 'https://www.bisnow.com/national/news/sustainability-climate/sustainability-rooftop-tournesol-siteworks-studiob-123623',
      isExternal: true
    }
  ]);

  const handleArticleClick = (article: Article) => {
    if (article.isExternal && article.sourceUrl) {
      window.open(article.sourceUrl, '_blank');
    } else {
      setSelectedArticle(article);
    }
  };

  const getCategoryColor = (category: Article['category']) => {
    switch (category) {
      case 'technology': return '#000000';
      case 'industry': return '#000000';
      case 'insights': return '#000000';
      case 'tutorial': return '#000000';
      case 'news': return '#666666';
      default: return '#757575';
    }
  };

  return (
    <section id="articles" className="articles-section">
      <div className="articles-container">
        <h2 className="articles-title fade-up">Articles & Insights</h2>
        <p className="articles-subtitle fade-up">Curated insights on smart landscaping, PropTech, and sustainable commercial property management</p>
        
        <div className="articles-scroll-container fade-up">
          <div className="articles-scroll">
            {articles.map((article) => (
              <article 
                key={article.id} 
                className="article-card"
                onClick={() => handleArticleClick(article)}
              >
                {article.imageUrl && (
                  <div className="article-image">
                    <img src={article.imageUrl} alt={article.title} />
                  </div>
                )}
                <div className="article-content">
                  <span 
                    className="article-category"
                    style={{ backgroundColor: getCategoryColor(article.category) }}
                  >
                    {article.category}
                  </span>
                  <h3 className="article-title">{article.title}</h3>
                  <p className="article-excerpt">{article.excerpt}</p>
                  <div className="article-meta">
                    <span className="article-author">{article.author}</span>
                    <span className="article-date">
                      {new Date(article.date).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </div>
                  {article.isExternal && (
                    <span className="external-indicator">↗</span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>

      </div>

      {selectedArticle && !selectedArticle.isExternal && (
        <div className="article-modal" onClick={() => setSelectedArticle(null)}>
          <div className="article-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedArticle(null)}>×</button>
            <span 
              className="article-category"
              style={{ backgroundColor: getCategoryColor(selectedArticle.category) }}
            >
              {selectedArticle.category}
            </span>
            <h2>{selectedArticle.title}</h2>
            <div className="article-modal-meta">
              <span>{selectedArticle.author}</span>
              <span>{new Date(selectedArticle.date).toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}</span>
            </div>
            <div className="article-modal-body">
              {selectedArticle.content.split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Articles;